/**
 * Batch Vulnerability Remediation — body of the "Batch Remediation" tab inside
 * the "Zafran Remediation Tickets (JIRA)" section.
 *
 * The buckets come from the server, which groups the daily JIRA pull by the
 * verdict Bedrock assigned to each ticket (severity plus the scope of the code
 * change required) and caches those verdicts in S3:
 *   GET  /api/batch-remediation/{app}
 *   POST /api/batch-remediation/{app}/preview   body {bucket}
 *   POST /api/batch-remediation/{app}/trigger   body {bucket, runId, force?}
 *
 * That single read is the only source of the buckets. Re-deriving them here
 * from JIRA priority would show counts the preview and the trigger do not act
 * on, so a failed read renders an error rather than a plausible-looking view.
 *
 * The trigger always runs a preview first and waits for explicit confirmation.
 * Everyone sees the buckets; the action stays disabled unless the server
 * reports canTrigger. Preview and trigger are per application and cover every
 * epic - the epic selector filters this view only.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_ENDPOINTS } from '../config/apiConfig';
import {
  BUCKET_ORDER,
  BUCKET_STYLE,
  EPIC_UNASSIGNED,
  activeRunState,
  alreadyRanToday,
  bucketCount,
  capSummary,
  coverage,
  deferredByEpic,
  epicOptions,
  epicTotal,
  filterTicketsByEpic,
  formatAge,
  normalizeBuckets,
  previewOutcome,
  runOutcome,
  statusBadge,
} from './batchRemediationView';
import './BatchRemediationSection.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const WHOLE_APP_NOTE = 'Remediating a bucket covers the whole application, every epic.';

// How often the tab re-reads itself while a run is in flight. The run takes
// minutes (one Bedrock plan and one Copilot delegation per repository) and the
// per-ticket statuses land as each repository's session is accepted, so the
// view has to keep catching up rather than showing one snapshot.
const RUN_POLL_MS = 10000;

// Honest empty-state: a null/empty value renders as a dash rather than a blank.
const dash = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return v;
};

const fmtTimestamp = (ts) => {
  if (!ts) return '—';
  const ms = typeof ts === 'number' ? ts : Date.parse(ts);
  if (Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleString();
};

const bucketLabel = (name) => (BUCKET_STYLE[name] && BUCKET_STYLE[name].label) || name;

const KeyList = ({ keys }) => (
  <span className="batch-key-list">
    {(Array.isArray(keys) ? keys : []).map((k) => (
      <code key={k}>{k}</code>
    ))}
  </span>
);

const TicketRow = ({ ticket }) => {
  const key = ticket.key || ticket.jiraKey;
  const badge = statusBadge(ticket.remediationStatus);
  // Bedrock's evidence is absent for an UNCATEGORIZED ticket. Say so rather
  // than rendering a dash, which would read as a measured value.
  const assessed = !!ticket.aiSeverity;
  const evidence = [
    ticket.aiSeverity,
    ticket.aiChangeType,
    ticket.aiChangeScope ? `${ticket.aiChangeScope} scope` : '',
    ticket.aiConfidence !== null && ticket.aiConfidence !== undefined && ticket.aiConfidence !== ''
      ? `confidence ${ticket.aiConfidence}`
      : '',
  ].filter(Boolean).join(' · ');
  return (
    <div className="batch-ticket">
      <div className="batch-ticket-head">
        {ticket.url ? (
          <a className="batch-ticket-key" href={ticket.url} target="_blank" rel="noopener noreferrer">
            {dash(key)}
          </a>
        ) : (
          <span className="batch-ticket-key">{dash(key)}</span>
        )}
        {/* The unchanged JIRA priority next to Bedrock's bucket, so a
            disagreement between the two stays visible to the operator. */}
        <span className="batch-ticket-priority">JIRA priority: {dash(ticket.priority)}</span>
        <span className="batch-ticket-bucket">Bedrock bucket: {dash(ticket.bucket)}</span>
        <span className="batch-ticket-epic">
          EPIC: {ticket.parentKey ? ticket.parentKey : 'none'}
        </span>
      </div>
      <div className="batch-ticket-summary">{dash(ticket.summary)}</div>
      {assessed ? (
        <div className="batch-ticket-ai" title={ticket.aiRationale || undefined}>
          Bedrock assessment: {evidence}
        </div>
      ) : (
        <div className="batch-ticket-ai unassessed">Not yet assessed</div>
      )}
      {badge && (
        <div className="batch-ticket-meta">
          <span className={`batch-ticket-status tone-${badge.tone}`}>
            {badge.tone === 'running' ? '⏳ ' : ''}{badge.label}
          </span>
        </div>
      )}
    </div>
  );
};

// Progress of the run in flight. Shown until the server stops reporting an
// active run, which is the only signal that the fan-out finished: the trigger
// returns as soon as the run is accepted, long before any pull request exists.
const RunProgress = ({ run, onRefresh, refreshing }) => {
  const age = formatAge(run.ageSeconds);
  return (
    <div className={`batch-run-progress ${run.stale ? 'stale' : ''}`}>
      <div className="batch-run-progress-head">
        <span className="batch-run-progress-state">
          {run.stale ? '⚠️' : <span className="spinner-small" aria-hidden="true" />}
          {run.stale
            ? ` The ${bucketLabel(run.bucket)} bucket run stopped reporting`
            : ` Remediating the ${bucketLabel(run.bucket)} bucket…`}
        </span>
        <button className="batch-btn secondary" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>
      {run.stale ? (
        <p className="batch-run-progress-detail">
          The run was accepted {age ? `${age} ago` : 'a while ago'} but never
          recorded an outcome, so it is no longer executing. Any pull request it
          did open is still listed against its tickets below. Check the
          {' '}<code>icop-jira-batch-remediation</code> function&apos;s logs, then
          preview the bucket again to retry the tickets it did not remediate.
        </p>
      ) : (
        <p className="batch-run-progress-detail">
          {run.ticketCount} ticket(s) authorised. Bedrock plans each repository
          and then one GitHub Copilot session per repository opens its pull
          request, so this takes a few minutes. Each ticket below moves to
          "Pull request opened" as its repository's session is accepted. This
          view refreshes itself every {Math.round(RUN_POLL_MS / 1000)} seconds —
          you can leave the page.
        </p>
      )}
      <p className="batch-preview-meta">
        Run <code>{dash(run.runId)}</code>
        {run.branchHint ? <> · branch <code>{run.branchHint}</code></> : null}
        {age ? ` · started ${age} ago` : ''}
        {run.triggeredBy ? ` · by ${run.triggeredBy}` : ''}
      </p>
    </div>
  );
};

// The daily-cap line, shared by the preview and the run-result panels.
const CapNote = ({ cap }) => {
  if (!cap || !cap.capped) return null;
  const perEpic = Object.entries(cap.byEpic || {})
    .filter(([, a]) => a && a.limit !== null && a.limit !== undefined)
    .map(([epic, a]) => `${epic === EPIC_UNASSIGNED ? 'No epic' : epic}: ${a.remaining} left`);
  return (
    <div className="batch-cap-note">
      <strong>
        {cap.selected} of {cap.eligible} eligible tickets will be remediated today
        {cap.limit !== null ? ` (daily cap ${cap.limit})` : ''}.
      </strong>
      {perEpic.length > 0 && (
        <span className="batch-cap-epics"> Remaining per EPIC — {perEpic.join(' · ')}.</span>
      )}
    </div>
  );
};

// Confirmation plan rendered from the preview response before the user commits
// to the trigger. Lists each repository with the ticket keys it will cover, the
// prospective branches/PRs, what idempotency excluded and what the daily cap
// deferred.
const PreviewPlan = ({ preview, bucket, statusByKey, onConfirm, onCancel, triggering, canTrigger }) => {
  const repos = Array.isArray(preview.repositories) ? preview.repositories : [];
  const prs = Array.isArray(preview.prospectivePullRequests) ? preview.prospectivePullRequests : [];
  const all = Array.isArray(preview.tickets) ? preview.tickets : [];
  const eligible = Array.isArray(preview.eligibleTickets) ? preview.eligibleTickets : [];
  const excluded = Array.isArray(preview.skippedTickets) ? preview.skippedTickets : [];
  const deferred = Array.isArray(preview.deferredTickets) ? preview.deferredTickets : [];
  const outcome = previewOutcome(preview);
  const cap = capSummary(preview);
  // A run already ran today for this bucket, so a plain confirm would be
  // refused by the one-run-per-day guard. The button becomes an explicit
  // force action instead of a dead end.
  const ranToday = alreadyRanToday(preview);
  return (
    <div className="batch-preview">
      <h4 className="batch-preview-title">
        Confirm remediation — {bucketLabel(bucket)} bucket
      </h4>
      <p className="batch-preview-hint">
        Review the plan below. Nothing is changed until you confirm. Confirming opens
        one pull request per repository ({prs.length} total) and never merges automatically.
        The run covers the whole application and every epic, whatever the epic filter shows.
      </p>
      <p className="batch-preview-meta">
        Run <code>{dash(preview.runId)}</code> · branch <code>{dash(preview.branchHint)}</code> ·
        {' '}{dash(preview.date)}
      </p>

      <CapNote cap={cap} />

      <div className="batch-preview-grid">
        <div className="batch-preview-card">
          <div className="batch-preview-value">{all.length}</div>
          <div className="batch-preview-label">Tickets in bucket</div>
        </div>
        <div className="batch-preview-card">
          <div className="batch-preview-value">{cap ? cap.selected : eligible.length}</div>
          <div className="batch-preview-label">Tickets to remediate</div>
        </div>
        <div className="batch-preview-card">
          <div className="batch-preview-value">{repos.length}</div>
          <div className="batch-preview-label">Repositories</div>
        </div>
        <div className="batch-preview-card">
          <div className="batch-preview-value">{prs.length}</div>
          <div className="batch-preview-label">Prospective PRs</div>
        </div>
        <div className="batch-preview-card">
          <div className="batch-preview-value">{excluded.length}</div>
          <div className="batch-preview-label">Excluded (in flight or already remediated)</div>
        </div>
      </div>

      {repos.length > 0 && (
        <table className="batch-preview-table">
          <thead>
            <tr>
              <th>Repository</th>
              <th>Base branch</th>
              <th>Prospective branch</th>
              <th>Tickets</th>
            </tr>
          </thead>
          <tbody>
            {repos.map((r) => (
              <tr key={r.repository}>
                <td className="batch-repo-name">{dash(r.repository)}</td>
                <td>{dash(r.branch)}</td>
                <td><code>{dash(r.branchHint)}</code></td>
                <td>
                  {Array.isArray(r.tickets) && r.tickets.length > 0
                    ? <KeyList keys={r.tickets} />
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {outcome === 'empty-bucket' && (
        <p className="batch-preview-warn">
          ⚠️ The server's {bucketLabel(bucket)} bucket is empty, so there is nothing to
          remediate. If the tab above still lists tickets for this bucket, the Bedrock
          categorization cache is older than the ticket pull and will catch up on the
          next categorization run.
        </p>
      )}
      {outcome === 'all-excluded' && (
        <p className="batch-preview-warn">
          ⚠️ All {all.length} ticket(s) in this bucket are already in flight or
          remediated, so nothing would be opened.
        </p>
      )}
      {outcome === 'cap-exhausted' && (
        <p className="batch-preview-warn">
          ⚠️ {eligible.length} ticket(s) are eligible, but this application's daily
          remediation allowance is already used up. They stay queued for tomorrow.
        </p>
      )}
      {outcome === 'no-repositories' && (
        <p className="batch-preview-warn">
          ⚠️ {eligible.length} eligible ticket(s), but no repository could be resolved for
          them, so nothing would be opened. Check the application's configured
          repositories in "Manage repositories &amp; config".
        </p>
      )}

      {excluded.length > 0 && (
        <div className="batch-preview-skipped">
          <strong>Excluded (in flight or already remediated).</strong> A ticket whose newest
          remediation record is analyzing, generating_fix, pr_created, pr_merged or verified
          is left alone so a second pull request is never opened for work already under way.
          <ul className="batch-reason-list">
            {excluded.map((k) => (
              <li key={k}>
                <code>{k}</code> — {statusByKey[k] || 'no recorded status'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {deferred.length > 0 && (
        <div className="batch-preview-skipped">
          <strong>Deferred by the daily cap.</strong> These tickets are eligible and nothing
          is wrong with them — today's allowance is simply spent, so they are picked up on a
          later run. This is not the same as the excluded list above.
          <ul className="batch-reason-list">
            {deferred.map((d) => (
              <li key={d.key}>
                <code>{d.key}</code> — {d.reason === 'epic-cap' ? 'epic daily cap' : 'application daily cap'}
                {d.epic ? ` (${d.epic === EPIC_UNASSIGNED ? 'no epic' : d.epic})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ranToday && outcome === 'ok' && (
        <p className="batch-preview-warn">
          ⚠️ A batch run for the {bucketLabel(bucket)} bucket already ran today. Running
          again will open fresh pull requests for the {eligible.length} ticket(s) that are
          still eligible — tickets already in flight stay excluded. Use "Run again anyway"
          only if the earlier run did not open the pull requests you expected.
        </p>
      )}

      <div className="batch-preview-actions">
        <button className="batch-btn secondary" onClick={onCancel} disabled={triggering}>
          Cancel
        </button>
        <button
          className="batch-btn primary"
          onClick={() => onConfirm({ force: ranToday })}
          disabled={triggering || outcome !== 'ok' || !canTrigger}
          title={
            !canTrigger
              ? 'You do not have permission to trigger batch remediation.'
              : outcome !== 'ok'
                ? 'There is nothing this run would open.'
                : ranToday
                  ? `Force a second run today for this bucket. ${WHOLE_APP_NOTE}`
                  : `Open one PR per repository for this bucket. ${WHOLE_APP_NOTE}`
          }
        >
          {triggering
            ? '⏳ Triggering…'
            : ranToday
              ? `Run again anyway — ${bucketLabel(bucket)} bucket`
              : `Remediate ${bucketLabel(bucket)} bucket`}
        </button>
      </div>
    </div>
  );
};

// Outcome of a confirmed run, per repository. A repo whose plan failed is
// rendered distinctly from one whose Copilot run failed: the first opened
// nothing by design, the second tried and did not finish.
const RunResult = ({ result }) => {
  const repos = Array.isArray(result.repositories) ? result.repositories : [];
  const skipped = Array.isArray(result.skippedTickets) ? result.skippedTickets : [];
  const cap = capSummary(result);
  return (
    <div className="batch-run-result">
      <div className="batch-run-head">
        ✅ Batch run dispatched for the {bucketLabel(result.bucket)} bucket —
        {' '}{repos.length} repository session(s).
      </div>
      <p className="batch-preview-meta">
        Run <code>{dash(result.runId)}</code> · branch <code>{dash(result.branchHint)}</code> ·
        {' '}{dash(result.date)}
      </p>
      <CapNote cap={cap} />
      {skipped.length > 0 && (
        <p className="batch-preview-skipped">
          Excluded (in flight or already remediated): <KeyList keys={skipped} />
        </p>
      )}
      {repos.map((repo) => {
        const outcome = runOutcome(repo);
        return (
          <div key={repo.repository} className={`batch-run-repo ${outcome}`}>
            <div className="batch-run-repo-head">
              <span className="batch-repo-name">{dash(repo.repository)}</span>
              {outcome === 'plan-failed' && (
                <span className="batch-run-state">
                  Bedrock could not produce a remediation plan, so no pull request was opened
                </span>
              )}
              {outcome === 'failed' && (
                <span className="batch-run-state">The Copilot run failed</span>
              )}
              {outcome === 'ok' && <span className="batch-run-state">Dispatched</span>}
              {repo.prUrl && (
                <a className="batch-run-pr" href={repo.prUrl} target="_blank" rel="noopener noreferrer">
                  View pull request
                </a>
              )}
            </div>
            {repo.error && <div className="batch-run-error">{repo.error}</div>}
            {Array.isArray(repo.succeeded) && repo.succeeded.length > 0 && (
              <div className="batch-run-line">Dispatched: <KeyList keys={repo.succeeded} /></div>
            )}
            {Array.isArray(repo.failed) && repo.failed.length > 0 && (
              <div className="batch-run-line">Failed: <KeyList keys={repo.failed} /></div>
            )}
            {Array.isArray(repo.noChange) && repo.noChange.length > 0 && (
              <div className="batch-run-line">No change needed: <KeyList keys={repo.noChange} /></div>
            )}
            {Array.isArray(repo.plannedTickets) && repo.plannedTickets.length > 0 && (
              <div className="batch-run-line">Planned: <KeyList keys={repo.plannedTickets} /></div>
            )}
            {Array.isArray(repo.unplannedTickets) && repo.unplannedTickets.length > 0 && (
              <div className="batch-run-line">
                In the same pull request without a planned step:
                {' '}<KeyList keys={repo.unplannedTickets} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const BatchRemediationSection = ({ appId, onRefresh }) => {
  // The whole GET response. It carries the buckets, the epic breakdown, the
  // coverage counts and the server's trigger decision, so it is the only
  // source this tab renders from.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [httpStatus, setHttpStatus] = useState(null);
  const [errorText, setErrorText] = useState(null);
  // Epic filter: '' = all epics. Display only - the trigger stays per
  // application (see WHOLE_APP_NOTE).
  const [epicFilter, setEpicFilter] = useState('');
  // Bucket currently in the preview/confirm flow, its preview payload, and any
  // run result. Only one bucket flows at a time.
  const [activeBucket, setActiveBucket] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Set while a poll is in flight, so the periodic refresh does not blank the
  // buckets out and flash the loading spinner every ten seconds.
  const [refreshing, setRefreshing] = useState(false);

  const fetchBuckets = useCallback(async ({ quiet = false } = {}) => {
    if (!appId) return;
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setErrorText(null);
    setHttpStatus(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.get(`${API_URL}${API_ENDPOINTS.batchRemediation(appId)}`);
      setData(res.data || null);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error loading batch remediation buckets:', err);
      }
      // Keep the status and the server's own message. Swallowing this read is
      // what previously turned a 404 into a tab that looked like it worked.
      setData(null);
      setHttpStatus(err.response?.status || null);
      setErrorText(err.response?.data?.error || err.message || 'Failed to load the buckets');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [appId]);

  useEffect(() => { fetchBuckets(); }, [fetchBuckets]);

  const activeRun = useMemo(() => activeRunState(data), [data]);

  // Poll only while a run is actually in flight. The trigger returns as soon as
  // the run is accepted and the per-ticket outcomes land over the following
  // minutes, so without this the operator would be looking at a snapshot taken
  // before any of the work happened. A stale run is deliberately excluded: it
  // will never report, and polling it forever would just spin.
  useEffect(() => {
    if (!activeRun || !activeRun.running) return undefined;
    const id = setInterval(() => { fetchBuckets({ quiet: true }); }, RUN_POLL_MS);
    return () => clearInterval(id);
  }, [activeRun, fetchBuckets]);

  // A finished run changes the Open/Fixed tabs too (its tickets gain a pull
  // request), so tell the parent once, on the transition out of running.
  const wasRunning = useRef(false);
  useEffect(() => {
    const running = !!(activeRun && activeRun.running);
    if (wasRunning.current && !running && onRefresh) onRefresh();
    wasRunning.current = running;
  }, [activeRun, onRefresh]);

  const startPreview = useCallback(async (bucket) => {
    setActiveBucket(bucket);
    setPreview(null);
    setRunResult(null);
    setActionError(null);
    setPreviewing(true);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.post(
        `${API_URL}${API_ENDPOINTS.batchRemediationPreview(appId)}`,
        { bucket }
      );
      const plan = res.data?.preview || null;
      // A 200 with no usable plan is a failure, not an empty plan: opening the
      // panel on it would show a column of zeros that reads as a measurement.
      if (previewOutcome(plan) === 'no-plan') {
        setActionError('The server returned no remediation plan for this bucket. Try again.');
        setActiveBucket(null);
        return;
      }
      setPreview(plan);
    } catch (err) {
      console.error('Error building batch preview:', err);
      setActionError(err.response?.data?.error || err.message || 'Failed to build the remediation plan');
      setActiveBucket(null);
    } finally {
      setPreviewing(false);
    }
  }, [appId]);

  const cancelPreview = useCallback(() => {
    setActiveBucket(null);
    setPreview(null);
    setActionError(null);
  }, []);

  const confirmTrigger = useCallback(async ({ force = false } = {}) => {
    if (!activeBucket || !preview || !preview.runId) return;
    setTriggering(true);
    setActionError(null);
    try {
      const axios = (await import('axios')).default;
      const res = await axios.post(
        `${API_URL}${API_ENDPOINTS.batchRemediationTrigger(appId)}`,
        { bucket: activeBucket, runId: preview.runId, force }
      );
      // Two shapes. The normal one is 202 accepted: the run is booked and a
      // worker is doing the fan-out, so there is no outcome to render yet and
      // the progress banner takes over from the next read. An inline run (no
      // worker available) returns its finished summary as `result`.
      setRunResult(res.data?.result || null);
      setPreview(null);
      setActiveBucket(null);
      // Re-read our own buckets so the booked run and the tickets it moved to
      // in-progress show immediately, and let the parent re-pull so the
      // Open/Fixed tabs agree.
      await fetchBuckets({ quiet: true });
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error triggering batch remediation:', err);
      // A 409 the server marked forceable (a run already ran today) is not a
      // dead end: confirming with force clears it. A replay or drift 409 is
      // not forceable - the preview has to be rebuilt.
      const data = err.response?.data || {};
      if (err.response?.status === 409 && data.forceable && !force) {
        setActionError(
          `${data.error || 'A run already ran for this bucket today.'} `
          + 'Use "Run again anyway" to remediate the remaining tickets.'
        );
      } else {
        setActionError(data.error || err.message || 'Failed to trigger remediation');
      }
    } finally {
      setTriggering(false);
    }
  }, [activeBucket, preview, appId, onRefresh, fetchBuckets]);

  const buckets = useMemo(() => normalizeBuckets(data), [data]);
  const epics = useMemo(() => epicOptions(data), [data]);
  const statusByKey = useMemo(() => {
    const out = {};
    BUCKET_ORDER.forEach((name) => {
      (buckets[name].tickets || []).forEach((t) => {
        const key = t.key || t.jiraKey;
        if (key && t.remediationStatus) out[key] = t.remediationStatus;
      });
    });
    return out;
  }, [buckets]);

  const canTrigger = !!(data && data.canTrigger);
  const cover = coverage(data, epicFilter);
  const deferredPerEpic = deferredByEpic(preview);
  // One run at a time per application: the daily cap is application-wide, so a
  // second bucket started mid-run would be planned against an allowance the
  // first run has not finished spending.
  const runInFlight = !!(activeRun && activeRun.running);

  if (loading) {
    return (
      <div className="batch-tab-body">
        <div className="jira-loading"><div className="spinner-small" /><span>Loading buckets…</span></div>
      </div>
    );
  }

  // The application has no JIRA EPIC in this environment, which is not the
  // same as "you are not an admin". The server reports it as configured:false;
  // a 404 is still accepted for a backend deployed before that change.
  const notConfigured = (data && data.configured === false) || httpStatus === 404;

  // The read is the only source of the buckets, so a failed read renders the
  // failure.
  if (errorText || notConfigured) {
    return (
      <div className="batch-tab-body">
        <div className={`batch-error ${notConfigured ? 'notice' : ''}`}>
          <span>
            {notConfigured ? (
              <>
                ℹ️ Batch remediation is not available for <strong>{appId}</strong> in this
                environment: it has no JIRA EPIC configured, so there is no ticket set to
                categorize. Add one under "Manage repositories &amp; config" above.
              </>
            ) : (
              <>⚠️ {errorText}</>
            )}
          </span>
          <button onClick={fetchBuckets}>Retry</button>
        </div>
      </div>
    );
  }

  const hasData = !!(data && data.hasData);
  const total = data && typeof data.total === 'number' ? data.total : null;

  return (
    <div className="batch-tab-body">
      <p className="batch-updated">
        Last pull {fmtTimestamp(data && data.lastUpdated)}
      </p>

      {cover && (
        <p className="batch-coverage">
          {cover.categorized} of {cover.total} ticket(s) categorized by Bedrock
          {epicFilter ? ` in ${epicFilter === EPIC_UNASSIGNED ? 'tickets with no epic' : epicFilter}` : ''}.
          {cover.uncategorized > 0 && (
            <span> The {cover.uncategorized} not yet assessed cannot be bulk-remediated.</span>
          )}
        </p>
      )}

      {!canTrigger && (
        <div className="batch-readonly-note">
          You can view the buckets. Triggering a batch run requires the
          iCOP.Vulnerability.Admin role.
        </div>
      )}

      {actionError && <div className="batch-error"><span>⚠️ {actionError}</span></div>}

      {!hasData ? (
        <div className="batch-empty-note">
          The daily JIRA pull has not landed for this application, so there are no
          buckets to show yet.
        </div>
      ) : total === 0 ? (
        <div className="batch-empty-note">
          The daily JIRA pull has no open remediation tickets for this application.
        </div>
      ) : (
        <>
          {activeRun && (
            <RunProgress
              run={activeRun}
              onRefresh={() => fetchBuckets({ quiet: true })}
              refreshing={refreshing}
            />
          )}

          {runResult && <RunResult result={runResult} />}

          {activeBucket && preview && (
            <PreviewPlan
              preview={preview}
              bucket={activeBucket}
              statusByKey={statusByKey}
              onConfirm={confirmTrigger}
              onCancel={cancelPreview}
              triggering={triggering}
              canTrigger={canTrigger}
            />
          )}

          {epics.length > 0 && (
            <div className="batch-filters">
              <select
                value={epicFilter}
                onChange={(e) => setEpicFilter(e.target.value)}
                title="Filter tickets by EPIC"
                aria-label="Filter tickets by EPIC"
              >
                <option value="">All EPICs</option>
                {epics.map((ep) => {
                  const count = epicTotal(data, ep);
                  const label = ep === EPIC_UNASSIGNED ? 'No epic (UNASSIGNED)' : ep;
                  return (
                    <option key={ep} value={ep}>
                      {label}{count != null ? ` (${count})` : ''}
                    </option>
                  );
                })}
              </select>
              <span className="batch-filter-note">
                The filter changes this view only. {WHOLE_APP_NOTE}
                {Object.keys(deferredPerEpic).length > 0 && (
                  <span>
                    {' '}Deferred today by the daily cap —
                    {' '}{Object.entries(deferredPerEpic)
                      .map(([ep, n]) => `${ep === EPIC_UNASSIGNED ? 'No epic' : ep}: ${n}`)
                      .join(' · ')}.
                  </span>
                )}
              </span>
            </div>
          )}

          <div className="batch-buckets">
            {BUCKET_ORDER.map((name) => {
              const section = buckets[name];
              const count = bucketCount(data, name, epicFilter);
              const bucketTickets = filterTicketsByEpic(section.tickets, epicFilter);
              const style = BUCKET_STYLE[name];
              const isActive = activeBucket === name;
              return (
                <div key={name} className="batch-bucket" style={{ borderColor: style.color }}>
                  <div className="batch-bucket-head">
                    <span className="batch-bucket-title" style={{ color: style.color }}>
                      {style.icon} {style.label}
                    </span>
                    <span className="batch-bucket-count">{count} tickets</span>
                    {section.triggerable ? (
                      <button
                        className="batch-btn primary"
                        onClick={() => startPreview(name)}
                        disabled={!canTrigger || previewing || triggering
                          || count === 0 || runInFlight}
                        title={
                          !canTrigger
                            ? 'Triggering requires the iCOP.Vulnerability.Admin role.'
                            : runInFlight
                              ? 'A batch run is already in flight for this application.'
                              : count === 0
                                ? 'No tickets in this bucket.'
                                : `Preview the plan before remediating this bucket. ${WHOLE_APP_NOTE}`
                        }
                      >
                        {isActive && previewing ? '⏳ Building plan…' : 'Remediate this bucket'}
                      </button>
                    ) : (
                      <span className="batch-bucket-note">
                        These tickets have no Bedrock assessment yet, so they cannot be
                        bulk-remediated.
                      </span>
                    )}
                  </div>
                  {bucketTickets.length === 0 ? (
                    <div className="batch-bucket-empty">No tickets in this bucket.</div>
                  ) : (
                    <div className="batch-ticket-list">
                      {bucketTickets.map((t) => (
                        <TicketRow key={t.key || t.jiraKey} ticket={t} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default BatchRemediationSection;
